import { useEffect } from "react";
import { Form, Input, Grid } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import "./LoginModal.scss";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { showModal } from "@/store/slices/modalSlice";
import SignUpForm from "../signup/SignUp";
import ForgotPassword from "../forgetPassword/forgotPassword";
import Button from "@/common/atoms/Button";
import { loginRequest, resetError } from "../duck/auth";
import GoogleLoginButton from "@/common/atoms/Button/GoogleLoginButton";
import { ErrorAlert } from "@/common";
import { useTranslation } from "react-i18next";
import HomesGlobeSmallIcon from "@/assets/brand/HomesGlobeSmallIcon";
import AuthHeading from "../common/AuthHeading";
import BrnadInfoAuth from "../common/BrandInfoAuth";
import AuthOrDivider from "../common/AuthOrDivider";
import PPandTOU from "../common/PPandTOU";
import FormLabel from "../common/FormLabel";

const LoginComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { error, loading, isVisible } = useSelector(
    (state) => ({
      error: state.auth.error,
      loading: state.auth.loading,
      isVisible: state.modal.isVisible
    }),
    shallowEqual
  );

  const [form] = Form.useForm();

  const onFinish = (values) => {
    dispatch(loginRequest(values));
  };

  const onSignUpClick = () => {
    dispatch(resetError());
    dispatch(
      showModal({
        content: <SignUpForm />,
        classes: "sign-up-modal-container"
      })
    );
  };
  const onForgetPasswordClick = () => {
    dispatch(resetError());
    dispatch(
      showModal({
        content: <ForgotPassword />,
        classes: "forgot-password-modal-container"
      })
    );
  };

  useEffect(() => {
    dispatch(resetError());
    form.resetFields();
  }, [isVisible]);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const isMobile = !screens.lg;

  return (
    <div className="flex gap-1 relative login-modal">
      <div className="bg-white rounded-lg mt-[40px] p-8 flex-[2] relative login-modal overflow-hidden flex flex-col justify-evenly">
        <HomesGlobeSmallIcon />
        <AuthHeading
          title={t("login.welcomeBack")}
          subTitle={t("login.dontHaveAccount")}
          onClickTitle={t("common.signup")}
          onClick={onSignUpClick}
        />
        {/* FORM */}
        {error ? <ErrorAlert error={error} /> : null}
        <Form form={form} name="login" onFinish={onFinish} layout="vertical">
          <Form.Item
            name="email"
            label={<FormLabel label={t("common.email")} />}
            rules={[
              { required: true, message: t("common.emailRequired") },
              { type: "email", message: t("common.emailInvalid") }
            ]}>
            <Input
              placeholder={t("common.enterEmail")}
              className="rounded-md h-10 flex items-center justify-center"
            />
          </Form.Item>
          <Form.Item
            className="mb-3"
            name="password"
            label={<FormLabel label={t("common.password")} />}
            rules={[{ required: true, message: t("common.passwordRequired") }]}>
            <Input.Password
              placeholder={t("common.enterPassword")}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#7B9E7E" />
                ) : (
                  <EyeInvisibleOutlined />
                )
              }
              className="rounded-md h-10 flex items-center justify-center "
            />
          </Form.Item>

          <Form.Item className="mb-2">
            <p
              className="font-sans font-normal cursor-pointer text-brandGray2 text-xs hover:underline"
              onClick={onForgetPasswordClick}>
              {t("forgot.forgotPassword")}?
            </p>
          </Form.Item>

          <Form.Item className="mb-0">
            <Button
              loading={loading}
              htmlType="submit"
              className="w-full py-2 h-[37px]">
              {t("common.login")}
            </Button>
          </Form.Item>
        </Form>
        {/* FORM */}

        <AuthOrDivider title={t("common.or")} />
        <GoogleLoginButton />
        <PPandTOU />
      </div>
      {!isMobile && (
        <div className="flex-[1.5]  h-auto">
          <BrnadInfoAuth
            title={t("brandInfoAuth.title")}
            subTitle1={t("brandInfoAuth.subTitle1")}
            subTitle2={t("brandInfoAuth.subTitle2")}
          />
        </div>
      )}
    </div>
  );
};

export default LoginComponent;
